import { action, makeAutoObservable } from "mobx";
import { UserProfile } from "@/models/UserProfile";
import { authStore, interactionStore, profileStore } from "./Stores";
import { mapCognitoAttributesToUserProfile } from "@/util/userProfileMapper";
import type { ProfileService } from "@/services/ProfileService";
import { getCookie } from "@/util/cookieUtils";
import { profileService } from "@/services/Services";
export class ProfileStore {
  private static instance: ProfileStore;
  private profileService: ProfileService;

  public profile: UserProfile = new UserProfile({});
  public isEditing = false;
  public token = "";
  public showErrors = false;
  public profileMissing = false;
  public secret2FA: string | null = null;
  public verificationCode2FA: string[] = ["", "", "", "", "", ""];
  public saveStatus?: "success" | "error" | null = null;
  public passwordChangeStatus?: "success" | "error";

  private constructor() {
    makeAutoObservable(this);
    this.profileService = profileService;

    const urlParams = new URLSearchParams(window.location.search);
    this.profileMissing = urlParams.get("profileMissing") === "1";
    this.token = getCookie("access_token") ?? "";
  }

  public static getInstance(): ProfileStore {
    if (!ProfileStore.instance) {
      ProfileStore.instance = new ProfileStore();
    }
    return ProfileStore.instance;
  }

  public isCurrentSession(sessionId: string): boolean {
    if (!this.profile.sessions.length) return false;

    // Find the session with the given ID
    const session = this.profile.sessions.find(s => s.session_id === sessionId);

    // Return true if the session exists and has current=true
    return !!session?.current;
  }

  @action
  public setProfile(profile: Partial<UserProfile>) {
    this.profile = new UserProfile(profile);
  }

  @action
  public setEditing(value: boolean) {
    this.isEditing = value;
  }

  public setToken(token: string) {
    console.log("Debug - ProfileStore token updated");
    this.token = token;
  }

  private getLatestToken(): string {
    // Always get the latest token from cookies as fallback
    return (this.token || getCookie("access_token")) ?? "";
  }

  public async uploadAvatar(file: File): Promise<void> {
    if (!file) return;

    const formData = new FormData();
    formData.append("avatar", file);
    formData.append("access_token", this.getLatestToken());

    interactionStore.loading = true;
    try {
      await this.profileService.uploadAvatar(file, this.getLatestToken());
      await this.fetchProfile(); // Refresh profile to get new avatar URL
    } catch (error) {
      console.error("Failed to upload avatar:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async enable2FA(): Promise<void> {
    interactionStore.loading = true;
    try {
      const response = await this.profileService.enable2FA();
      if (response.data) {
        this.secret2FA = response.data.secret_code;
        console.log("2FA enabled, secret code:", this.secret2FA);
      }
    } catch (error) {
      console.error("Failed to enable 2FA:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async disable2FA(): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.disable2FA();
      this.profile.twoFactorAuth = "no"; // Disable 2FA in profile
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async deleteAccount(): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.deleteAccount();
      profileStore.logout(); // Logout after account deletion
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async verify2FA(): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.verify2FA(profileStore.verificationCode2FA.join(""));
      profileStore.verificationCode2FA = ["", "", "", "", "", ""]; // Reset verification code
      profileStore.secret2FA = null; // Clear secret after verification
      this.profile.twoFactorAuth = "yes"; // Enable 2FA in profile
    } catch (error) {
      console.error("Failed to verify 2FA:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async saveProfile(): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.updateProfile(this.profile.toJSON());
      await authStore.refreshToken();
      this.setEditing(false);
      this.profileMissing = false;
      this.saveStatus = "success";
    } catch (error) {
      console.error("Failed to save profile:", error);
      this.saveStatus = "error";
    } finally {
      interactionStore.loading = false;
    }
  }

  public async fetchProfile(): Promise<void> {
    interactionStore.loading = true;
    try {
      const response = await this.profileService.getProfile();
      if (response?.data) {
        const userProfile = mapCognitoAttributesToUserProfile(response.data);
        this.setProfile(userProfile);
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  @action
  public async revokeSession(sessionId: string): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.revokeSession(sessionId);
      await this.fetchProfile(); // Refresh profile to get updated sessions
    } catch (error) {
      console.error("Failed to revoke session:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  @action
  public async logout(): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.revokeSession();
      authStore.logout();
    } catch (error) {
      console.error("Failed to revoke session:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  public async passwordChange(current_password: string, new_password: string): Promise<void> {
    interactionStore.loading = true;
    try {
      await this.profileService.passwordChange(current_password, new_password);
      this.passwordChangeStatus = "success";
    } catch (error) {
      this.passwordChangeStatus = "error";
      console.error("Failed to change password:", error);
    } finally {
      interactionStore.loading = false;
    }
  }
}
